import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import {
  ROLL, ROLL_SLOTS, LEVELS, PART_MAX, fillIn, slotOrder, isWritable, isMine, isShown, unlockedNow, partWords, rollSlot, rollAll, writePart,
  levelDone, nextLevel, nextToRoll, wordsOf, rollSentence, sentenceSaid, shapeOf, pieceOf, type Roll, type RollSlot, type RollWords, type SaidPart,
} from '../data/imagine-copy';
import { IdeaPostcard, Die } from './IdeaPostcard';

/** A roll longer than this goes to two smaller lines in its row; written words, a smaller size. */
const LONG = 22;
const lengthClass = (s: string) => (s.length > 30 ? 'longer' : s.length > 18 ? 'long' : '');

/** The least the postcard's picture gives way to on a short phone: the figures still in it, head to toe. */
const ART_MIN = 100;

/**
 * The idea roll (data/imagine-copy.ts ROLL, well/product/33 §9): [who] at
 * [where], [doing what]. A path across the top says where they are (two
 * levels, then their picture); a postcard draws what the dice dealt; three
 * rows under it. Level 1 opens on three question marks and one big button,
 * the dice: rolling is the thing to do, and only once they have rolled is
 * there a Level 2. There the dice go: the doing row comes up empty and is
 * theirs to write, the other two keep their rolls as words to change or
 * leave. "Make it" is never greyed out: tapped before the doing row is
 * written, it puts the cursor there and says so (W4's replays: a dead Next
 * on Level 3, tapped and tapped, then gone). A small "skip", once anything
 * is rolled, goes on, the rolls standing in for anything not written.
 *
 * Nothing moves under the thumb while they roll: the rows are one line each,
 * the caption and the line under the path have fixed heights.
 *
 * Controlled: the screen owns the roll (so a reload brings it back) and does
 * the counting; this tells it what happened.
 */
export function IdeaRoll({ roll, onRoll, goal, firstLine, onMake, onSpin, onLevel }: {
  roll: Roll;
  onRoll: (r: Roll) => void;
  /** over everything, on every level: what this idea is for ("An idea for your podcast") */
  goal: string;
  /** Level 1's line under the path: it follows from the question before */
  firstLine: string;
  /** go on to "Make your first creation" with this sentence: made at Level 4, or skipped to from anywhere */
  onMake: (w: RollWords, how: 'made' | 'skipped') => void;
  /** a die they rolled */
  onSpin?: (slot: RollSlot | 'all') => void;
  /** a level they went on to */
  onLevel?: (level: number) => void;
}) {
  const last = roll.level >= LEVELS;
  const done = levelDone(roll);
  const fresh = unlockedNow(roll);
  const shape = shapeOf(roll);
  // the rows in the sentence's order, which is the order the levels open them in
  const order = slotOrder(shape);
  // Level 1 rolls one die at a time, the rows after it not yet shown, so it is plain where to tap
  const rollNext = nextToRoll(roll);
  const anyRolled = ROLL_SLOTS.some((slot) => isShown(roll, slot));
  // "Make it" tapped before the row they must write is written: the line says so until they have
  const [asked, setAsked] = useState(false);
  const line = roll.level === 1
    ? (done ? ROLL.rolled : anyRolled && rollNext ? fillIn(ROLL.rollNextLine, { slot: shape.noun[rollNext] }) : firstLine)
    : done ? ROLL.written : asked && fresh ? fillIn(ROLL.nowWrite, { slot: shape.labels[fresh] }) : shape.turn[fresh ?? 'doing'];
  const rollEverything = () => { onRoll(rollAll(roll)); onSpin?.('all'); };
  const rollTheNext = () => { if (!rollNext) return; onRoll(rollSlot(roll, rollNext)); onSpin?.(rollNext); };
  const nextLabel = ROLL.nextIs;
  // what to do right now, said when they tap the words of a row they cannot write in yet (Level 1 only: after it, every row is written in)
  const nowWhat = rollNext ? fillIn(ROLL.nowRoll, { slot: shape.noun[rollNext] }) : fillIn(ROLL.nowNext, { next: nextLabel });

  // the row being written in, lit in the sentence too, so they can see which part they are changing
  const [editing, setEditing] = useState<RollSlot | null>(null);
  // a tap on the words of a row they cannot write in yet: the row shows its lock and says when it opens,
  // and the button that gets them there (the dice, or the way on) asks to be pressed
  const [nudge, setNudge] = useState<{ slot: RollSlot; at: number } | null>(null);
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(null), 1800);
    return () => clearTimeout(t);
  }, [nudge]);
  const piece = (slot: RollSlot) => pieceOf(roll, slot);
  const said = ({ slot, lead, text }: SaidPart): ReactNode => {
    const lit = editing === slot ? ' editing' : '';
    if (!text) return <span className={`blank${lit}`}>____</span>;
    return <>{lead}<span className={`${isMine(roll, slot) ? 'yours' : 'dealt'}${lit}`}>{text}</span></>;
  };
  // the sentence in the shape the dream and kind called for, as it will be said: "Me and [who] talking about [doing] [every when]"
  const words = { who: partWords(roll, 'who'), where: partWords(roll, 'where'), doing: partWords(roll, 'doing') };
  const caption = <>{sentenceSaid(words, shape).map((p, i) => <Fragment key={i}>{typeof p === 'string' ? p : said(p)}</Fragment>)}</>;
  const label = rollSentence({ who: partWords(roll, 'who') || ROLL.wrapped, where: partWords(roll, 'where') || ROLL.wrapped, doing: partWords(roll, 'doing') || ROLL.wrapped }, shape);

  const go = () => {
    if (!done) {
      // the cursor goes to the row to write, inside this tap, so iOS raises the keyboard
      if (fresh) { setAsked(true); document.getElementById(`roll-${fresh}`)?.focus(); }
      return;
    }
    if (last) { onMake(wordsOf(roll), 'made'); return; }
    const n = nextLevel(roll);
    // the row it unlocks is drawn now, inside this tap, and gets the cursor: iOS only raises the keyboard
    // for a focus that happens in the tap itself, never one a moment later
    flushSync(() => onRoll(n));
    const slot = unlockedNow(n);
    if (slot) document.getElementById(`roll-${slot}`)?.focus();
    onLevel?.(roll.level + 1);
  };

  // On a short phone (375x667 inside Facebook's browser) the whole step is taller than the screen. The
  // picture gives back exactly what overflows, down to ART_MIN (cropped from the top: the ground and the
  // figures stay), measured because the caption is one line or three depending on the words, and again
  // whenever anything around it changes height. Anything still over scrolls; nothing overlaps.
  const root = useRef<HTMLDivElement>(null);
  const fitKey = `${roll.level}|${done}`;
  useLayoutEffect(() => {
    const main = root.current?.closest('.main');
    const fit = () => {
      const art = root.current?.querySelector<SVGSVGElement>('.postcard-art');
      if (!art || !main) return;
      art.style.height = '';
      // twice: sub-pixel heights can leave a pixel over after the first
      for (let i = 0; i < 2; i++) {
        const over = main.scrollHeight - main.clientHeight;
        if (over <= 0 || art.clientHeight <= ART_MIN) break;
        art.style.height = `${Math.max(ART_MIN, art.clientHeight - over)}px`;
      }
    };
    fit();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => fit()) : null;
    for (const el of [main, ...(main?.children ?? []), root.current?.querySelector('.postcard-caption'), root.current?.querySelector('.roll-rows')]) {
      if (el && el !== root.current) ro?.observe(el);
    }
    window.addEventListener('resize', fit);
    return () => { ro?.disconnect(); window.removeEventListener('resize', fit); };
  }, [fitKey]);

  return (
    <>
      <h1 className="roll-goal">{goal}</h1>
      <RollPath level={roll.level} line={line} cue={ROLL.cue[roll.level]} />
      <div className="roll" ref={root}>
        <IdeaPostcard who={piece('who')} where={piece('where')} doing={piece('doing')} caption={caption} label={label} fixedCaption />
        <div className="roll-rows">
          {order.map((slot, i) => {
            const name = shape.labels[slot];
            if (!isWritable(roll, slot)) {
              // not theirs to write yet: its die rolls it; its words, tapped, say when it will be theirs
              const shown = isShown(roll, slot);
              // a row after the next die is not shown yet; it keeps its place so nothing moves when it comes
              const waiting = !shown && slot !== rollNext;
              const nudged = nudge?.slot === slot;
              // the die that would do it shakes, so the eye goes from the words to it (owner, 2026-10-29);
              // the row it sits in holds still, so the die is the thing that moves
              const dieNudged = !!nudge && slot === rollNext;
              const why = nowWhat;
              return (
                <div key={`${slot}-dice`} className={`roll-row dice ${shown ? '' : 'unrolled'} ${waiting ? 'not-yet' : ''} ${slot === rollNext ? 'up-next' : ''} ${nudged ? 'nudged' : ''} ${dieNudged ? 'die-says' : ''}`} aria-hidden={waiting || undefined}>
                  <span className="roll-label">{name}</span>
                  <button type="button" className="roll-text" tabIndex={waiting ? -1 : undefined} onClick={() => setNudge({ slot, at: Date.now() })} aria-label={`${name}: ${shown ? roll[slot].rolled : '?'}. ${why}`}>
                    {nudged ? <span key={nudge!.at} className="roll-why"><Lock /> <span>{why}</span></span> : shown ? <span className={`roll-words ${roll[slot].rolled.length > LONG ? 'long' : ''}`}>{roll[slot].rolled}</span> : '?'}
                  </button>
                  <button key={dieNudged ? nudge!.at : 'still'} type="button" className={`roll-die ${dieNudged ? 'nudged' : ''}`} tabIndex={waiting ? -1 : undefined} onClick={() => { setNudge(null); onRoll(rollSlot(roll, slot)); onSpin?.(slot); }} aria-label={fillIn(shown ? ROLL.rollOne : ROLL.rollThe, { slot: shape.noun[slot] })}>
                    <Die />
                  </button>
                </div>
              );
            }
            const yours = isMine(roll, slot);
            const turn = slot === fresh && !yours;
            return (
              <div key={`${slot}-write`} className={`roll-row write ${yours ? 'mine' : ''} ${turn ? 'your-turn' : ''}`}>
                <label className="roll-label" htmlFor={`roll-${slot}`}>{name}</label>
                <input
                  id={`roll-${slot}`}
                  className={`roll-input ${lengthClass(roll[slot].mine)}`}
                  type="text"
                  value={roll[slot].mine}
                  placeholder={shape.ask[slot]}
                  maxLength={PART_MAX}
                  autoComplete="off"
                  autoCapitalize={slot === 'who' ? 'words' : 'none'}
                  spellCheck={false}
                  enterKeyHint={slot === fresh || i === order.length - 1 ? 'done' : 'next'}
                  onChange={(e) => onRoll(writePart(roll, slot, e.target.value))}
                  // the roll is still in it: a tap selects it, so typing replaces it and a tap more edits it
                  onFocus={(e) => { setEditing(slot); if (!yours) { const el = e.currentTarget; setTimeout(() => el.setSelectionRange(0, el.value.length), 0); } }}
                  onBlur={() => setEditing((x) => (x === slot ? null : x))}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    e.currentTarget.blur();
                    go();
                  }}
                />
                <span className="roll-pen" aria-hidden="true"><Pen /></span>
                {/* the die this level took away, tumbling off the row */}
                {slot === fresh && <span className="roll-die die-away" aria-hidden="true"><Die /></span>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="stack roll-done" style={{ marginTop: 'auto', gap: 4 }}>
        {roll.level === 1 ? (
          // Level 1: the dice are the thing to do; once rolled, rolling again sits beside the way on, the same height
          done ? (
            <div className="roll-buttons">
              <button type="button" className="btn butter roll-again" onClick={rollEverything}><Die size={20} /> {ROLL.rollAgain}</button>
              <button type="button" className={`btn coral ${nudge ? 'nudged' : ''}`} onClick={go}>{nextLabel} ›</button>
            </div>
          ) : (
            <button type="button" className={`btn butter roll-big ${nudge ? 'nudged' : ''}`} onClick={rollTheNext}><Die size={24} /> {fillIn(ROLL.rollThe, { slot: shape.noun[rollNext!] })}</button>
          )
        ) : (
          <button className="btn coral" onClick={go}>{last ? ROLL.make : `${nextLabel} ›`}</button>
        )}
        {/* not before the first roll: a skip then would sketch words they never saw (QA, 2026-10-28); it keeps its place, so nothing moves when it comes */}
        <button type="button" className="link roll-skip" style={anyRolled ? undefined : { visibility: 'hidden' }} aria-hidden={!anyRolled || undefined} tabIndex={anyRolled ? undefined : -1} onClick={() => anyRolled && onMake(wordsOf(roll), 'skipped')}>{ROLL.skip}</button>
      </div>
    </>
  );
}

/** The levels and the picture at the end: where they are, the whole time. */
function RollPath({ level, line, cue }: { level: number; line: string; cue?: string }) {
  return (
    <div className="roll-path">
      <ol className="roll-stops" aria-label={`${fillIn(ROLL.level, { n: String(level) })} of ${LEVELS}`}>
        {Array.from({ length: LEVELS }, (_, i) => i + 1).map((n) => (
          <li key={n} className={n < level ? 'past' : n === level ? 'now' : ''} aria-current={n === level ? 'step' : undefined}>
            <span className="roll-stop">{n < level ? '✓' : n}</span>
          </li>
        ))}
        <li className="gift" aria-label={ROLL.picture}><span className="roll-stop"><Gift /></span></li>
      </ol>
      {/* the line and the cue under it keep one height together, so nothing below them moves from level to level */}
      <div className="roll-say">
        <p key={line} className="roll-line">{line}</p>
        <p className="roll-cue">{cue}</p>
      </div>
    </div>
  );
}

function Gift() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="10" width="17" height="11" rx="2" fill="#F2705B" />
      <rect x="2.5" y="7" width="19" height="4.5" rx="1.5" fill="#D9573F" />
      <rect x="10.8" y="7" width="2.4" height="14" fill="#FFD36B" />
      <ellipse cx="9" cy="5.6" rx="3.2" ry="2" fill="#FFD36B" transform="rotate(-20 9 5.6)" />
      <ellipse cx="15" cy="5.6" rx="3.2" ry="2" fill="#FFD36B" transform="rotate(20 15 5.6)" />
    </svg>
  );
}

function Lock() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <rect x="4.5" y="10" width="15" height="11" rx="3" fill="currentColor" />
    </svg>
  );
}

function Pen() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d="M11.2 1.8l3 3-8.6 8.6-3.8.8.8-3.8z" fill="currentColor" />
    </svg>
  );
}
